import React, { useState } from 'react';
import { supabase } from '../src/lib/supabaseClient';
import type { AuthLang } from './AuthModal';

interface PasswordResetScreenProps {
  language?: AuthLang;
  isDarkMode?: boolean;
  onDone: () => void;
}

const copy: Record<
  AuthLang,
  {
    title: string;
    desc: string;
    labelNew: string;
    labelConfirm: string;
    ph: string;
    submit: string;
    submitting: string;
    mismatch: string;
    tooShort: string;
    error: (e: string) => string;
  }
> = {
  en: {
    title: 'Set new password',
    desc: 'Choose a new password for your account.',
    labelNew: 'NEW PASSWORD',
    labelConfirm: 'CONFIRM PASSWORD',
    ph: 'At least 6 characters',
    submit: 'Update password',
    submitting: 'Saving…',
    mismatch: 'Passwords do not match.',
    tooShort: 'Password must be at least 6 characters.',
    error: (e) => e || 'Update failed.',
  },
  zh: {
    title: '设置新密码',
    desc: '请为您的账户设置新密码。',
    labelNew: '新密码',
    labelConfirm: '确认新密码',
    ph: '至少 6 位',
    submit: '更新密码',
    submitting: '保存中…',
    mismatch: '两次输入的密码不一致。',
    tooShort: '密码至少 6 位。',
    error: (e) => e || '更新失败。',
  },
};

const PasswordResetScreen: React.FC<PasswordResetScreenProps> = ({ language = 'zh', isDarkMode = true, onDone }) => {
  const t = copy[language] ?? copy.zh;
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const accent = '#D7CDA1';
  const bg = isDarkMode ? '#0a0a0a' : '#fafafa';
  const text = isDarkMode ? '#fff' : '#111';
  const muted = isDarkMode ? '#888' : '#666';
  const border = isDarkMode ? '#333' : '#e5e5e5';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    if (pwd.length < 6) {
      setMessage(t.tooShort);
      return;
    }
    if (pwd !== pwd2) {
      setMessage(t.mismatch);
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pwd });
      if (error) throw error;
      try {
        const { pathname, search } = window.location;
        window.history.replaceState({}, '', pathname + search);
      } catch {
        /* ignore */
      }
      await supabase.auth.signOut();
      onDone();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setMessage(t.error(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[5000] flex items-center justify-center p-6"
      style={{
        background: isDarkMode ? 'rgba(0,0,0,0.92)' : 'rgba(255,255,255,0.96)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <div
        className="w-full max-w-md rounded-2xl border p-8 shadow-2xl"
        style={{
          background: bg,
          borderColor: border,
          color: text,
          fontFamily: "'Inter', system-ui, sans-serif",
        }}
      >
        <h2 className="text-xl font-bold mb-2">{t.title}</h2>
        <p className="text-sm mb-6" style={{ color: muted }}>
          {t.desc}
        </p>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-xs tracking-wide mb-1" style={{ color: muted }}>
              {t.labelNew}
            </label>
            <input
              type="password"
              autoComplete="new-password"
              minLength={6}
              required
              placeholder={t.ph}
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              className="w-full rounded-lg border px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#D7CDA1]/40"
              style={{ borderColor: border, background: isDarkMode ? '#111' : '#fff', color: text }}
            />
          </div>
          <div>
            <label className="block text-xs tracking-wide mb-1" style={{ color: muted }}>
              {t.labelConfirm}
            </label>
            <input
              type="password"
              autoComplete="new-password"
              minLength={6}
              required
              placeholder={t.ph}
              value={pwd2}
              onChange={(e) => setPwd2(e.target.value)}
              className="w-full rounded-lg border px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#D7CDA1]/40"
              style={{ borderColor: border, background: isDarkMode ? '#111' : '#fff', color: text }}
            />
          </div>
          {message ? <p className="text-sm text-red-400">{message}</p> : null}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl font-semibold text-black disabled:opacity-50"
            style={{ background: accent }}
          >
            {loading ? t.submitting : t.submit}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PasswordResetScreen;
